import React from 'react'
import { useForm } from '@inertiajs/react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Button } from '@/components/ui/button'
import { Building2, User } from 'lucide-react'
import type CrmFundraisingOpportunity from '@/types/serializers/CrmFundraisingOpportunity'

interface EditOpportunitySheetProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  opportunity: CrmFundraisingOpportunity
}

interface OpportunityForm {
  opportunity: {
    name: string
    estimated_amount: string
    description: string
    expected_close_date: string
  }
}

export function EditOpportunitySheet({
  open,
  onOpenChange,
  opportunity,
}: EditOpportunitySheetProps) {
  const amountTND = (opportunity.estimated_amount_millimes / 1000).toFixed(0)

  const form = useForm<OpportunityForm>({
    opportunity: {
      name: opportunity.name,
      estimated_amount: amountTND,
      description: opportunity.description ?? '',
      expected_close_date: opportunity.expected_close_date ?? '',
    },
  })

  function handleClose() {
    form.reset()
    onOpenChange(false)
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    form.patch(`/fundraising/opportunities/${opportunity.id}`, {
      onSuccess: () => {
        onOpenChange(false)
      },
    })
  }

  const isOrgDonor = opportunity.donor_type?.includes('OrganizationDonor')

  return (
    <Sheet open={open} onOpenChange={handleClose}>
      <SheetContent className="w-[520px] sm:max-w-[520px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Modifier l'opportunite</SheetTitle>
          <SheetDescription>
            Modifiez les informations de cette opportunite.
          </SheetDescription>
        </SheetHeader>

        <form onSubmit={handleSubmit} className="space-y-4 p-4">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="edit_opp_name">Nom *</Label>
            <Input
              id="edit_opp_name"
              value={form.data.opportunity.name}
              onChange={(e) =>
                form.setData('opportunity', {
                  ...form.data.opportunity,
                  name: e.target.value,
                })
              }
              required
              autoFocus
            />
            {form.errors['opportunity.name'] && (
              <p className="text-sm text-destructive">{form.errors['opportunity.name']}</p>
            )}
          </div>

          {/* Donor (read-only) */}
          {opportunity.donor_name && (
            <div className="space-y-2">
              <Label>Donateur</Label>
              <div className="flex items-center gap-2 rounded-md border px-3 py-2 bg-muted/50 text-sm">
                {isOrgDonor ? (
                  <Building2 className="h-4 w-4 text-muted-foreground shrink-0" />
                ) : (
                  <User className="h-4 w-4 text-muted-foreground shrink-0" />
                )}
                <span>{opportunity.donor_name}</span>
              </div>
            </div>
          )}

          {/* Amount */}
          <div className="space-y-2">
            <Label htmlFor="edit_opp_amount">Montant estime (TND)</Label>
            <Input
              id="edit_opp_amount"
              type="number"
              min="0"
              step="1"
              value={form.data.opportunity.estimated_amount}
              onChange={(e) =>
                form.setData('opportunity', {
                  ...form.data.opportunity,
                  estimated_amount: e.target.value,
                })
              }
              placeholder="0"
            />
            {form.errors['opportunity.estimated_amount'] && (
              <p className="text-sm text-destructive">{form.errors['opportunity.estimated_amount']}</p>
            )}
          </div>

          {/* Expected close date */}
          <div className="space-y-2">
            <Label htmlFor="edit_opp_close_date">Date de cloture prevue</Label>
            <Input
              id="edit_opp_close_date"
              type="date"
              value={form.data.opportunity.expected_close_date}
              onChange={(e) =>
                form.setData('opportunity', {
                  ...form.data.opportunity,
                  expected_close_date: e.target.value,
                })
              }
            />
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="edit_opp_description">Notes</Label>
            <Textarea
              id="edit_opp_description"
              value={form.data.opportunity.description}
              onChange={(e) =>
                form.setData('opportunity', {
                  ...form.data.opportunity,
                  description: e.target.value,
                })
              }
              placeholder="Notes internes sur cette opportunite..."
              rows={3}
            />
          </div>

          <div className="flex gap-3 pt-2">
            <Button type="submit" disabled={form.processing} className="flex-1">
              {form.processing ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
            <Button type="button" variant="outline" onClick={handleClose}>
              Annuler
            </Button>
          </div>
        </form>
      </SheetContent>
    </Sheet>
  )
}
